/**
 * SubmitToggleCell — toggles `is_selected_for_submit` for a measure.
 *
 * NOT optimistic (locked decision from V1 kickoff Q&A). Cell briefly
 * disables while the mutation is in flight; dashboard invalidation
 * repaints from server state.
 *
 * Pinned-mode behavior (V1.1 plan §5.6): when `pinned` is non-null the
 * checkbox is disabled with a tooltip explaining why. The pinned-mode
 * disable takes precedence over the in-flight disable.
 */
import { useToggleSubmission } from "@/hooks/useToggleSubmission";
import { type PinnedContext, pinnedTooltip } from "../types";

export interface SubmitToggleCellProps {
  clinicId: number;
  year: number;
  measureId: number;
  isSelected: boolean;
  pinned: PinnedContext | null;
}

export function SubmitToggleCell({
  clinicId,
  year,
  measureId,
  isSelected,
  pinned,
}: SubmitToggleCellProps) {
  const toggle = useToggleSubmission(clinicId);
  const isPinned = pinned !== null;

  const onChange = () => {
    if (isPinned) return;
    toggle.mutate({
      measureId,
      year,
      body: { is_selected: !isSelected },
    });
  };

  return (
    <input
      type="checkbox"
      checked={isSelected}
      disabled={toggle.isPending || isPinned}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      aria-label={isSelected ? "Selected for submit" : "Not selected for submit"}
      title={pinned ? pinnedTooltip(pinned) : undefined}
      className="h-4 w-4 cursor-pointer accent-tw-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  );
}
